// src/components/Boxes/Overview/overview.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import { buildAuthedJsonBody, getUserFromToken } from "../../../../utils/functions.ts";
import OverviewTransactions from "./overviewTransactions.component.jsx"; // adjust path if needed
import OverviewNotes from "./overviewNotes.component.jsx"; // ✅ NEW
import OverviewReports from "./overviewReports.component.jsx"; // ✅ NEW
import OverviewFiles from "./overviewFiles.component.jsx";
import "./overview.style.scss";

const STORAGE_KEY_POS = "OVERVIEW_BOX_LOCATION";
const DEFAULT_POS = { x: window.innerWidth - 1220 - 10, y: 100 };
const PRINT_SELECTION_DEFAULTS = {
    profile: true,
    transactions: true,
    reports: true,
    notes: true,
};

const Overview = () => {
    const playerCtx = usePlayer?.() || {};
    const player = playerCtx.activePlayer || playerCtx.player || playerCtx.selectedPlayer || {};
    const p = player || {};

    const [nums, setNums] = useState({
        reports: 0,
        notes: 0,
        transactions: 0,
        quick: 0,
    });
    const [loadingNums, setLoadingNums] = useState(false);
    const [printOpen, setPrintOpen] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const [printError, setPrintError] = useState("");
    const [printReports, setPrintReports] = useState([]);
    const [printNotes, setPrintNotes] = useState([]);
    const [printTransactions, setPrintTransactions] = useState([]);
    const [printSelections, setPrintSelections] = useState(PRINT_SELECTION_DEFAULTS);

    // "" | "Transactions" | "Reports" | "Notes" | "Files"
    const [whatButton, setWhatButton] = useState("");

    const toggleWhatButton = (key) => {
        setWhatButton((prev) => (prev === key ? "" : key));
    };

    // --------------------- counts fetch (already working) ---------------------
    useEffect(() => {
        const controller = new AbortController();

        const fetchPlayerNumbers = async () => {
            setNums({ reports: 0, notes: 0, transactions: 0, quick: 0 });

            if (!p?.FBXID) {
                setLoadingNums(false);
                return;
            }

            setLoadingNums(true);

            const token = await getUserFromToken().catch(() => null);
            if (!token?.team) {
                setLoadingNums(false);
                return;
            }

            try {
                const res = await fetch("https://www.fbxchange.com/ReactPHP/overview.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: controller.signal,
                    body: buildAuthedJsonBody({
                        FBXID: p.FBXID,
                        myTeam: token.team,
                        scout: token.scout,
                        script: "getPlayerNumbers",
                    }),
                });

                const raw = await res.text();
                let data;
                try {
                    data = JSON.parse(raw);
                } catch (e) {
                    console.error("overview.php getPlayerNumbers not JSON:", raw);
                    setLoadingNums(false);
                    return;
                }

                setNums({
                    reports: Number(data?.reports ?? 0),
                    notes: Number(data?.notes ?? 0),
                    transactions: Number(data?.transactions ?? 0),
                    quick: Number(data?.quick ?? 0),
                });

                setLoadingNums(false);
            } catch (err) {
                if (err?.name === "AbortError") return;
                console.error("Error fetching overview numbers:", err);
                setLoadingNums(false);
            }
        };

        fetchPlayerNumbers();
        return () => controller.abort();
    }, [p?.FBXID]);

    useEffect(() => {
        setPrintOpen(false);
        setPrintError("");
        setPrintReports([]);
        setPrintNotes([]);
        setPrintTransactions([]);
        setPrintSelections(PRINT_SELECTION_DEFAULTS);
    }, [p?.FBXID]);

    // --------------------- helpers ---------------------

    const cleanPart = (v) => {
        if (v === undefined || v === null) return "";
        const s = String(v).trim();
        if (!s) return "";

        const low = s.toLowerCase();
        if (low === "null" || low === "undefined" || low === "na" || low === "n/a") return "";
        if (s === "0000-00-00" || s === "1950-01-01") return "";
        return s;
    };

    const isMeaningfulText = (v) => {
        const s = cleanPart(v);
        if (!s) return false;
        const low = s.toLowerCase();
        return s !== "-" && s !== "0" && low !== "null" && low !== "undefined" && low !== "n/a" && low !== "na";
    };

    const fmtDate = (d) => {
        if (!d) return "";
        const s = String(d).trim();
        if (s === "1950-01-01" || s === "0000-00-00" || s === "null") return "";
        return s;
    };

    // "5040 X 2025-05-28"
    const fmtHws = (valIn, vIn, dIn) => {
        const val = valIn === undefined || valIn === null ? "" : String(valIn).trim();
        if (!val) return "-";
        const v = vIn === undefined || vIn === null ? "" : String(vIn).trim();
        const dt = fmtDate(dIn);

        let out = val;
        if (v) out += ` ${v}`;
        if (dt) out += ` ${dt}`;
        return out;
    };

    const fmtSpeedVal = (s) => {
        if (s === undefined || s === null || String(s).trim() === "") return "";
        const n = Number(s);
        if (Number.isNaN(n)) return String(s).trim();
        return n.toFixed(2);
    };

    const pickScoutId = (token) => (
        token?.UserId ??
        token?.userId ??
        token?.userid ??
        token?.ID ??
        token?.id ??
        token?.scoutnum ??
        token?.scoutNum ??
        token?.scout ??
        ""
    );

    const normalizeReportRow = (r) => {
        const id =
            r?.id ??
            r?.Dr_SR_Num ??
            r?.DR_SR_Num ??
            `${r?.FBXID || ""}-${r?.REPORT_DATE || r?.creationDate || Math.random()}`;

        return {
            id: String(id),
            reportDate: cleanPart(r?.REPORT_DATE) || cleanPart(r?.reportDate) || cleanPart(r?.creationDate) || "-",
            scout: cleanPart(r?.SCOUT) || cleanPart(r?.Scout) || cleanPart(r?.scoutName) || cleanPart(r?.scout) || cleanPart(r?.scoutnum) || "-",
            exposure: cleanPart(r?.exposure) || cleanPart(r?.P_GM_1) || "-",
            pos: cleanPart(r?.COL_POS1) || "-",
            draftYear: cleanPart(r?.DRAFT_YEAR) || "-",
            gradeCode: cleanPart(r?.gradeCode) || "-",
            finalVal: cleanPart(r?.FINAL) || "-",
            finalCalc: cleanPart(r?.Final_Calculated) || "-",
            isPrivate: cleanPart(r?.isPrivate) || "N",
            visibleTo: cleanPart(r?.VisibleTo) || "-",
            summary: isMeaningfulText(r?.SUMMARY) ? cleanPart(r?.SUMMARY) : "",
            strength: isMeaningfulText(r?.M_STRENGTH) ? cleanPart(r?.M_STRENGTH) : "",
            weakness: isMeaningfulText(r?.M_WEAKNESS) ? cleanPart(r?.M_WEAKNESS) : "",
            character: isMeaningfulText(r?.CHARIT) ? cleanPart(r?.CHARIT) : "",
            production: isMeaningfulText(r?.PRODUCTION) ? cleanPart(r?.PRODUCTION) : "",
            medical: isMeaningfulText(r?.COMPETITOR) ? cleanPart(r?.COMPETITOR) : "",
            environment: isMeaningfulText(r?.STRENGTH) ? cleanPart(r?.STRENGTH) : "",
        };
    };

    const normalizeNoteRow = (r) => {
        const id =
            r?.CommId ??
            r?.commId ??
            r?.ID ??
            r?.id ??
            r?.noteID ??
            r?.NoteID ??
            `${r?.FBXID || ""}-${r?.NoteDate || r?.noteDate || r?.DATE || r?.date || Math.random()}`;

        return {
            id: String(id),
            date: cleanPart(r?.NoteDate) || cleanPart(r?.DateOfContact) || cleanPart(r?.noteDate) || cleanPart(r?.DATE) || cleanPart(r?.date) || cleanPart(r?.createdAt) || cleanPart(r?.created_at) || "-",
            title: cleanPart(r?.Title) || cleanPart(r?.SourceData) || cleanPart(r?.title) || cleanPart(r?.NoteTitle) || cleanPart(r?.noteTitle) || cleanPart(r?.subject) || cleanPart(r?.SUBJECT) || "-",
            by: cleanPart(r?.ByWho) || cleanPart(r?.ByName) || cleanPart(r?.byWho) || cleanPart(r?.createdBy) || cleanPart(r?.CreatedBy) || cleanPart(r?.scoutName) || cleanPart(r?.ScoutName) || cleanPart(r?.displayName) || cleanPart(r?.userName) || cleanPart(r?.user) || cleanPart(r?.WHO) || "-",
            noteHtml: cleanPart(r?.Note) || cleanPart(r?.Communication) || cleanPart(r?.note) || cleanPart(r?.NoteText) || cleanPart(r?.noteText) || cleanPart(r?.Memo) || cleanPart(r?.memo) || cleanPart(r?.Notes) || cleanPart(r?.notes) || "",
        };
    };

    const normalizeTransactionRow = (t) => ({
        id: String(t?.ID ?? t?.TransactionRowID ?? t?.transactionID ?? `${t?.ProTeamDate || ""}-${t?.League || ""}-${Math.random()}`),
        date: cleanPart(t?.ProTeamDate) || cleanPart(t?.theDate) || cleanPart(t?.transDate) || cleanPart(t?.Date) || "-",
        team: cleanPart(t?.ProTeam) || cleanPart(t?.A_ProTeam) || cleanPart(t?.Team) || "-",
        status: cleanPart(t?.ProTeamStatus) || cleanPart(t?.Status) || "-",
        position: cleanPart(t?.PRO_POS) || cleanPart(t?.A_ProPos) || cleanPart(t?.Position) || "-",
        league: cleanPart(t?.League) || "-",
        year: cleanPart(t?.TeamYear) || "-",
        notes: cleanPart(t?.ST_NOTES) || cleanPart(t?.notes) || "-",
    });

    // --------------------- derived fields ---------------------
    const nameLine = useMemo(() => {
        if (p.NAME) return String(p.NAME);
        const last = p.LAST || p.last || p.lastName || "";
        const first = p.FIRST || p.first || p.firstName || "";
        const built = [last, first].filter(Boolean).join(", ");
        return built || "-";
    }, [p]);

    const cpLine = useMemo(() => String(p.CP || p.COL_POS1 || p.colPos1 || p.position || "-"), [p]);

    const ageLine = useMemo(() => {
        const a = p.AGE ?? p.age;
        return a !== undefined && a !== null && String(a).trim() !== "" ? String(a) : "-";
    }, [p]);

    const schoolLine = useMemo(() => {
        const longName = p.longSchool || "";
        const nickname = p.nickname || "";

        if (longName && nickname) {
            return `${longName} (${nickname})`;
        } else if (longName) {
            return String(longName);
        } else if (nickname) {
            return String(nickname);
        } else {
            return "-";
        }
    }, [p]);

    const heightLine = useMemo(
        () => fmtHws(p.height, p.heightV, p.heightDate),
        [p]
    );

    const weightLine = useMemo(
        () => fmtHws(p.weight, p.weightV, p.weightDate),
        [p]
    );

    const speedLine = useMemo(() => {
        const val = fmtSpeedVal(p.speed40Fastest);
        const v = p.speed40FVerified || "";
        const d = p.speed40FDate || "";
        return fmtHws(val, v, d);
    }, [p]);

    const cflLine = useMemo(() => {
        const parts = [
            cleanPart(p.cflTeam),
            cleanPart(p.cflStatus),
            cleanPart(fmtDate(p.cflDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.cflStatus, p.cflTeam, p.cflDate]);

    const nflLine = useMemo(() => {
        const parts = [
            cleanPart(p.nflTeam),
            cleanPart(p.nflStatus),
            cleanPart(fmtDate(p.nflDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.nflStatus, p.nflTeam, p.nflDate]);

    const uflLine = useMemo(() => {
        const parts = [
            cleanPart(p.ufl24Status),
            cleanPart(p.ufl24Team),
            cleanPart(fmtDate(p.ufl24Date)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.ufl24Status, p.ufl24Team, p.ufl24Date]);

    const draftLine = useMemo(() => {
        const yr = p.DRAFT_YEAR ?? p.nflDraftYear ?? "";
        const tm = p.DRAFTTEAM ?? p.nflDraftTeam ?? "";
        const rd = p.nflDraftRound ? `Round: ${p.nflDraftRound}` : "";
        const parts = [yr, tm, rd].filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
        return parts.length ? parts.join(" - ") : "-";
    }, [p]);

    const cfldraftLine = useMemo(() => {
        const yr = p.cflDraftYear ?? "";
        const tm = p.cflDraftTeam ?? "";
        const rd = p.cflDraftRound ? `Round: ${p.cflDraftRound}` : "";
        const parts = [yr, tm, rd].filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
        return parts.length ? parts.join(" - ") : "-";
    }, [p]);

    const eventLine = useMemo(() => {
        const parts = [
            cleanPart(p.EVENT || p.theEvent || p.event),
            cleanPart(fmtDate(p.EVENTDATE || p.theEventDate || p.eventDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.EVENT, p.theEvent, p.event, p.EVENTDATE, p.theEventDate, p.eventDate]);

    const reportCount = nums.reports ?? 0;
    const notesCount = nums.notes ?? 0;
    const transCount = nums.transactions ?? 0;
    const quickCount = nums.quick ?? 0;
    const showFilesPanel = whatButton === "Files";
    const titleLine = `${nameLine} | CP: ${cpLine} | Age: ${ageLine} | ${schoolLine}`;
    const buildPrintSelections = (current, reports, notes) => {
        const nextSelections = {
            ...PRINT_SELECTION_DEFAULTS,
            ...current,
        };

        reports.forEach((report) => {
            const key = `report-${report.id}`;
            nextSelections[key] = typeof current[key] === "boolean" ? current[key] : true;
        });
        notes.forEach((note) => {
            const key = `note-${note.id}`;
            nextSelections[key] = typeof current[key] === "boolean" ? current[key] : true;
        });

        return nextSelections;
    };
    const isPrintItemSelected = (type, id) => printSelections[`${type}-${id}`] !== false;

    // --------------------- EXPAND STYLE (keeps within DragBox) ---------------------
    const expanded =
        whatButton === "Transactions" ||
        whatButton === "Notes" ||
        whatButton === "Reports" ||
        showFilesPanel;

    const outerStyle = expanded
        ? { height: "720px", overflow: "hidden" }
        : { minHeight: 420 };

    const fetchPrintData = async ({ showLoading = true } = {}) => {
        setPrintError("");

        if (!p?.FBXID) {
            setPrintError("Select a player before printing.");
            return null;
        }

        if (showLoading) setPrintLoading(true);

        const token = await getUserFromToken().catch(() => null);
        if (!token?.team) {
            if (showLoading) setPrintLoading(false);
            setPrintError("Missing user/team token.");
            return null;
        }

        try {
            const fetchJson = async (label, url, payload) => {
                try {
                    const response = await fetch(url, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: buildAuthedJsonBody(payload),
                    });
                    const raw = await response.text();
                    let data;
                    try {
                        data = JSON.parse(raw);
                    } catch {
                        throw new Error("backend did not return JSON.");
                    }
                    if (!response.ok || data?.success === false) {
                        throw new Error(data?.error || "load failed.");
                    }
                    return data;
                } catch (error) {
                    throw new Error(`${label}: ${error?.message || "load failed."}`);
                }
            };

            const [reportsResult, transactionsResult, notesResult] = await Promise.allSettled([
                fetchJson("Reports", "https://www.fbxchange.com/ReactPHP/scoutreports.php", {
                    scriptName: "getReports",
                    ID: Number(p.FBXID),
                    team: token.team,
                    scout: pickScoutId(token),
                }),
                fetchJson("Transactions", "https://www.fbxchange.com/ReactPHP/overview.php", {
                    FBXID: Number(p.FBXID),
                    myTeam: token.team,
                    script: "getTransactions",
                }),
                fetchJson("Notes", "https://www.fbxchange.com/ReactPHP/overview.php", {
                    FBXID: Number(p.FBXID),
                    myTeam: token.team,
                    script: "getNotes",
                }),
            ]);

            const reportsData = reportsResult.status === "fulfilled" ? reportsResult.value : null;
            const transactionsData = transactionsResult.status === "fulfilled" ? transactionsResult.value : null;
            const notesData = notesResult.status === "fulfilled" ? notesResult.value : null;
            const reportPack = reportsData?.data || {};
            const reports = (Array.isArray(reportPack?.reports) ? reportPack.reports : []).map(normalizeReportRow);
            const transactions = (Array.isArray(transactionsData) ? transactionsData : []).map(normalizeTransactionRow).slice(0, 5);
            const notes = (Array.isArray(notesData) ? notesData : notesData?.rows || []).map(normalizeNoteRow);
            const errors = [reportsResult, transactionsResult, notesResult]
                .filter((result) => result.status === "rejected")
                .map((result) => result.reason?.message || "Load failed.");
            const nextSelections = buildPrintSelections(printSelections, reports, notes);

            setPrintReports(reports);
            setPrintTransactions(transactions);
            setPrintNotes(notes);
            setPrintSelections(nextSelections);
            setPrintError(errors.join(" "));
            return { reports, transactions, notes, selections: nextSelections };
        } catch (error) {
            setPrintError(error?.message || "Unable to load print data.");
            return null;
        } finally {
            if (showLoading) setPrintLoading(false);
        }
    };

    const openPrintOptions = () => {
        const nextOpen = !printOpen;
        setPrintOpen(nextOpen);
        if (nextOpen && p?.FBXID && printReports.length === 0 && printNotes.length === 0 && printTransactions.length === 0) {
            fetchPrintData();
        }
    };

    const togglePrintSelection = (key) => {
        setPrintSelections((current) => ({
            ...current,
            [key]: !current[key],
        }));
    };

    const setPrintItemSelection = (groupKey, itemKey, checked) => {
        setPrintSelections((current) => ({
            ...current,
            [groupKey]: checked ? true : current[groupKey],
            [itemKey]: checked,
        }));
    };

    const setPrintGroupSelection = (type, checked) => {
        setPrintSelections((current) => {
            const next = {
                ...current,
                [type]: checked,
            };
            if (type === "reports") {
                printReports.forEach((report) => {
                    next[`report-${report.id}`] = checked;
                });
            }
            if (type === "notes") {
                printNotes.forEach((note) => {
                    next[`note-${note.id}`] = checked;
                });
            }
            return next;
        });
    };

    const escapeHtml = (value) => String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const textBlock = (label, value) => {
        if (!isMeaningfulText(value)) return "";
        return `<section class="print-block"><h4>${escapeHtml(label)}</h4><div>${escapeHtml(value).replace(/\n/g, "<br>")}</div></section>`;
    };

    const printOverview = async () => {
        const needsPrintData =
            (printSelections.transactions && transCount > 0 && printTransactions.length === 0) ||
            (printSelections.reports && reportCount > 0 && printReports.length === 0) ||
            (printSelections.notes && notesCount > 0 && printNotes.length === 0);

        let reportsForPrint = printReports;
        let transactionsForPrint = printTransactions;
        let notesForPrint = printNotes;
        let selectionsForPrint = printSelections;

        if (needsPrintData) {
            const loadedPrintData = await fetchPrintData();
            if (!loadedPrintData) return;
            reportsForPrint = loadedPrintData.reports;
            transactionsForPrint = loadedPrintData.transactions;
            notesForPrint = loadedPrintData.notes;
            selectionsForPrint = loadedPrintData.selections;
        }

        const reportsToPrint = selectionsForPrint.reports
            ? reportsForPrint.filter((report) => selectionsForPrint[`report-${report.id}`] !== false)
            : [];
        const notesToPrint = selectionsForPrint.notes
            ? notesForPrint.filter((note) => selectionsForPrint[`note-${note.id}`] !== false)
            : [];
        const transactionsToPrint = selectionsForPrint.transactions ? transactionsForPrint.slice(0, 5) : [];

        if (!selectionsForPrint.profile && transactionsToPrint.length === 0 && reportsToPrint.length === 0 && notesToPrint.length === 0) {
            setPrintError("Choose at least one item to print.");
            return;
        }

        const transactionHtml = transactionsToPrint.map((transaction) => `
            <tr>
                <td>${escapeHtml(transaction.date)}</td>
                <td>${escapeHtml(transaction.league)}</td>
                <td>${escapeHtml(transaction.team)}</td>
                <td>${escapeHtml(transaction.status)}</td>
                <td>${escapeHtml(transaction.position)}</td>
                <td>${escapeHtml(transaction.year)}</td>
                <td>${escapeHtml(transaction.notes)}</td>
            </tr>
        `).join("");

        const reportHtml = reportsToPrint.map((report) => `
            <article class="print-card">
                <h3>Scout Report Details - ${escapeHtml(report.reportDate)}</h3>
                <div class="print-meta">
                    <span><b>Scout</b> ${escapeHtml(report.scout)}</span>
                    <span><b>Pos</b> ${escapeHtml(report.pos)}</span>
                    <span><b>Grade</b> ${escapeHtml(report.finalVal)}</span>
                    <span><b>Calc</b> ${escapeHtml(report.finalCalc)}</span>
                    <span><b>Codes</b> ${escapeHtml(report.gradeCode)}</span>
                    <span><b>Exposure</b> ${escapeHtml(report.exposure)}</span>
                    <span><b>Visible To</b> ${escapeHtml(report.visibleTo)}</span>
                </div>
                ${textBlock("Summary", report.summary)}
                ${textBlock("Positive", report.strength)}
                ${textBlock("Negative", report.weakness)}
                ${textBlock("Character/Mental", report.character)}
                ${textBlock("Production", report.production)}
                ${textBlock("Medical", report.medical)}
                ${textBlock("Environment", report.environment)}
            </article>
        `).join("");

        const noteHtml = notesToPrint.map((note) => `
            <article class="print-card">
                <h3>Player Note Details - ${escapeHtml(note.title)}</h3>
                <div class="print-meta">
                    <span><b>Date</b> ${escapeHtml(note.date)}</span>
                    <span><b>By</b> ${escapeHtml(note.by)}</span>
                </div>
                <div class="print-note">${DOMPurify.sanitize(note.noteHtml || "<em>No note content.</em>", { USE_PROFILES: { html: true } })}</div>
            </article>
        `).join("");

        const printWindow = window.open("", "FBXOverviewPrint", "width=980,height=720");
        if (!printWindow) {
            setPrintError("The browser blocked the print window.");
            return;
        }

        printWindow.document.open();
        printWindow.document.write(`
            <!doctype html>
            <html>
                <head>
                    <title>${escapeHtml(nameLine)} Overview</title>
                    <style>
                        body { margin: 28px; color: #172033; font-family: Arial, sans-serif; font-size: 12px; }
                        h1 { margin: 0 0 4px; color: #1f4f7d; font-size: 22px; }
                        h2 { margin: 22px 0 8px; border-bottom: 2px solid #1f4f7d; color: #1f4f7d; font-size: 16px; }
                        h3 { margin: 0 0 8px; color: #333366; font-size: 14px; }
                        h4 { margin: 10px 0 3px; color: #344054; font-size: 12px; text-transform: uppercase; }
                        .subtitle { margin-bottom: 16px; color: #555; }
                        .print-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px 14px; margin-bottom: 14px; }
                        .print-grid span, .print-meta span { display: block; }
                        .print-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
                        .print-table th { background: #eef4fb; color: #1f4f7d; text-align: left; }
                        .print-table th, .print-table td { padding: 6px; border: 1px solid #cbd5e1; vertical-align: top; }
                        .print-card { break-inside: avoid; margin-bottom: 14px; padding: 10px; border: 1px solid #cbd5e1; border-radius: 5px; }
                        .print-meta { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px 12px; margin-bottom: 8px; color: #475467; }
                        .print-block div, .print-note { line-height: 1.45; white-space: normal; }
                        @media print { body { margin: 18px; } .print-card { break-inside: avoid; } }
                    </style>
                </head>
                <body>
                    <h1>${escapeHtml(nameLine)}</h1>
                    <div class="subtitle">CP: ${escapeHtml(cpLine)} | Age: ${escapeHtml(ageLine)} | ${escapeHtml(schoolLine)}</div>
                    ${selectionsForPrint.profile ? `
                        <h2>Player Overview Data</h2>
                        <div class="print-grid">
                            <span><b>Height</b> ${escapeHtml(heightLine)}</span>
                            <span><b>Weight</b> ${escapeHtml(weightLine)}</span>
                            <span><b>Speed</b> ${escapeHtml(speedLine)}</span>
                            <span><b>CFL</b> ${escapeHtml(cflLine)}</span>
                            <span><b>NFL</b> ${escapeHtml(nflLine)}</span>
                            <span><b>UFL 24</b> ${escapeHtml(uflLine)}</span>
                            <span><b>CFL Draft</b> ${escapeHtml(cfldraftLine)}</span>
                            <span><b>NFL Draft</b> ${escapeHtml(draftLine)}</span>
                            <span><b>Event</b> ${escapeHtml(eventLine)}</span>
                        </div>
                    ` : ""}
                    ${transactionsToPrint.length > 0 ? `
                        <h2>Last 5 Player Transactions</h2>
                        <table class="print-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>League</th>
                                    <th>Team</th>
                                    <th>Status</th>
                                    <th>Pos</th>
                                    <th>Year</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>${transactionHtml}</tbody>
                        </table>
                    ` : ""}
                    ${reportsToPrint.length > 0 ? `<h2>Selected Scout Reports</h2>${reportHtml}` : ""}
                    ${notesToPrint.length > 0 ? `<h2>Selected Player Notes</h2>${noteHtml}` : ""}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    return (
        <DragBox2026
            storageKey={STORAGE_KEY_POS}
            defaultPos={DEFAULT_POS}
            title={titleLine}
            countText=""
            minWidth={1220}
            maxWidth={1220}
            zIndex={500}
            showReset={true}
            wideTitle={true}
        >
            <div className="container-fluid p-0" style={outerStyle}>
                <div className="row g-0 h-100">
                    <div className="col-48 h-100">
                        <div className="bg-info-subtle rounded-3 p-3 h-100 d-flex flex-column">
                            {/* ===================== OVERVIEW MAIN CARD ===================== */}
                            <div className="bg-white rounded-2 border p-3">
                                {/* H/W/S */}
                                {!expanded && (
                                <div className="row g-2 border-bottom pb-2 mb-2">
                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Height: </span>{heightLine}
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Weight: </span>{weightLine}
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Speed: </span>{speedLine}
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* League Status Bars */}
                                {!expanded && (
                                <div className="row g-2 mb-2">
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">CFL</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(220,53,69,.20)" }}>
                                            <div className="small">{cflLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">NFL</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(13,110,253,.20)" }}>
                                            <div className="small">{nflLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">UFL 24</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(255,193,7,.25)" }}>
                                            <div className="small">{uflLine}</div>
                                        </div>
                                    </div>
                                </div>
                                )}

                
                                {!expanded && (
                                <div className="row g-2">
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">CFL DRAFT YR - TEAM - ROUND</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(220,53,69,.20)" }}>
                                            <div className="small">{cfldraftLine}</div>
                                        </div>
                                    </div>
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">NFL DRAFT YR - TEAM - ROUND</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(13,110,253,.20)" }}>
                                            <div className="small">{draftLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">EVENT</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(111,66,193,.20)" }}>
                                            <div className="small">{eventLine}</div>
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* Buttons (Reports/Notes/Transactions toggle + ORANGE active) */}
                                <div className={`row g-2 ${expanded ? "mt-0" : "mt-2"}`}>
                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">SCOUT REPORTS</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Reports"
                                                ? "btn-warning"
                                                : (reportCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Reports")}
                                        >
                                            {loadingNums ? "REPORTS [...]" : `REPORTS [${reportCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">NOTES</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Notes"
                                                ? "btn-warning"
                                                : (notesCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Notes")}
                                        >
                                            {loadingNums ? "NOTES [...]" : `NOTES [${notesCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">TRANSACTIONS</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Transactions"
                                                ? "btn-warning"
                                                : (transCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Transactions")}
                                        >
                                            {loadingNums ? "TRANS [...]" : `TRANS [${transCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">FILES</div>
                                        <button
                                            type="button"
                                            className={`btn ${showFilesPanel
                                                ? "btn-warning"
                                                : (quickCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Files")}
                                        >
                                            {loadingNums ? "FILES [...]" : `FILES [${quickCount}]`}
                                        </button>
                                    </div>
                                </div>

                                <div className="row g-2 mt-2">
                                    <div className="col-48 d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${printOpen ? "btn-warning" : "btn-outline-primary"}`}
                                            onClick={openPrintOptions}
                                        >
                                            Print Options
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={printOverview}
                                            disabled={printLoading || !p?.FBXID}
                                        >
                                            {printLoading ? "Loading..." : "Print"}
                                        </button>
                                    </div>
                                </div>

                                {printOpen && (
                                    <div className="overview-print-options mt-2">
                                        <div className="overview-print-options-header">
                                            <strong>Print Overview</strong>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={fetchPrintData}
                                                disabled={printLoading}
                                            >
                                                {printLoading ? "Loading..." : "Refresh"}
                                            </button>
                                        </div>

                                        {printError && (
                                            <div className="alert alert-warning py-2 mb-2 fs-7">{printError}</div>
                                        )}

                                        <label className="overview-print-check">
                                            <input
                                                type="checkbox"
                                                checked={!!printSelections.profile}
                                                onChange={() => togglePrintSelection("profile")}
                                            />
                                            <span>Player overview</span>
                                        </label>

                                        <label className="overview-print-check mt-1">
                                            <input
                                                type="checkbox"
                                                checked={!!printSelections.transactions}
                                                onChange={() => togglePrintSelection("transactions")}
                                            />
                                            <span>Last 5 transactions [{printTransactions.length}]</span>
                                        </label>

                                        <div className="overview-print-select-grid">
                                            <section>
                                                <div className="overview-print-select-title">
                                                    <span>Reports [{printReports.length}]</span>
                                                    <div>
                                                        <button type="button" onClick={() => setPrintGroupSelection("reports", true)}>All</button>
                                                        <button type="button" onClick={() => setPrintGroupSelection("reports", false)}>None</button>
                                                    </div>
                                                </div>
                                                <label className="overview-print-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!printSelections.reports}
                                                        onChange={() => togglePrintSelection("reports")}
                                                    />
                                                    <span>Include selected reports</span>
                                                </label>
                                                <div className="overview-print-item-list">
                                                    {printLoading ? (
                                                        <div className="overview-print-empty">Loading reports...</div>
                                                    ) : printReports.length === 0 ? (
                                                        <div className="overview-print-empty">No reports found.</div>
                                                    ) : (
                                                        printReports.map((report) => (
                                                            <label className="overview-print-check overview-print-item" key={report.id}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isPrintItemSelected("report", report.id)}
                                                                    onChange={() => togglePrintSelection(`report-${report.id}`)}
                                                                />
                                                                <span title={`${report.reportDate} - ${report.scout}`}>
                                                                    {report.reportDate} - {report.scout} - Grade {report.finalVal}
                                                                </span>
                                                            </label>
                                                        ))
                                                    )}
                                                </div>
                                            </section>

                                            <section>
                                                <div className="overview-print-select-title">
                                                    <span>Notes [{printNotes.length}]</span>
                                                    <div>
                                                        <button type="button" onClick={() => setPrintGroupSelection("notes", true)}>All</button>
                                                        <button type="button" onClick={() => setPrintGroupSelection("notes", false)}>None</button>
                                                    </div>
                                                </div>
                                                <label className="overview-print-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!printSelections.notes}
                                                        onChange={() => togglePrintSelection("notes")}
                                                    />
                                                    <span>Include selected notes</span>
                                                </label>
                                                <div className="overview-print-item-list">
                                                    {printLoading ? (
                                                        <div className="overview-print-empty">Loading notes...</div>
                                                    ) : printNotes.length === 0 ? (
                                                        <div className="overview-print-empty">No notes found.</div>
                                                    ) : (
                                                        printNotes.map((note) => (
                                                            <label className="overview-print-check overview-print-item" key={note.id}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isPrintItemSelected("note", note.id)}
                                                                    onChange={() => togglePrintSelection(`note-${note.id}`)}
                                                                />
                                                                <span title={`${note.date} - ${note.title}`}>
                                                                    {note.date} - {note.title} - {note.by}
                                                                </span>
                                                            </label>
                                                        ))
                                                    )}
                                                </div>
                                            </section>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* ===================== END OVERVIEW MAIN CARD ===================== */}

	                            {/* ===================== EXPANDED NOTES PANEL ===================== */}
	                            {whatButton === "Notes" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewNotes
                                            FBXID={p?.FBXID}
                                            printSelections={printSelections}
                                            setPrintItemSelection={setPrintItemSelection}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED NOTES PANEL ===================== */}

	                            {/* ===================== EXPANDED TRANSACTIONS PANEL ===================== */}
	                            {whatButton === "Transactions" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewTransactions FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED TRANSACTIONS PANEL ===================== */}

	                            {/* ===================== EXPANDED REPORTS PANEL ===================== */}
	                            {whatButton === "Reports" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewReports
                                            FBXID={p?.FBXID}
                                            printSelections={printSelections}
                                            setPrintItemSelection={setPrintItemSelection}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED REPORTS PANEL ===================== */}

	                            {/* ===================== EXPANDED FILES PANEL ===================== */}
	                            {showFilesPanel ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "hidden", overflowX: "hidden" }}>
                                        <OverviewFiles FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED FILES PANEL ===================== */}
                        </div>
                    </div>
                </div>
            </div>
        </DragBox2026>
    );
};

export default Overview;
